import {useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {
  setBlanketType,
  setFirstWallType,
  setPrimaryCoolantType,
  setSecondaryCoolantType,
  setNeutronMultiplier,
  setStructure
} from "../../../store/reducers/configurationSlice";
import {
  BackButton,
  Button,
  ButtonsWrapper,
  Container,
  ContentCard,
  Dropdown,
  DropdownContainer,
  Form,
  Label, LabelWrapper,
  Title
} from "../SharedStyles";
import { ValidationError } from "./styled";
import InfoTooltip from "../../Shared/InfoTooltip";
import {
  FIRST_WALL_TYPE_OPTIONS,
  BLANKET_TYPE_OPTIONS,
  COOLANT_TYPE_OPTIONS,
  SECONDARY_COOLANT_TYPE_OPTIONS,
  NEUTRON_MULTIPLIER_OPTIONS,
  STRUCTURAL_MATERIAL_OPTIONS,
} from "../../../utils/blanketDisplayText";


const BlanketConfiguration = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [validationError, setValidationError] = useState(false);

  const firstWallType = useSelector(state => state.configuration.input.blanket.firstWallType);
  const blanketType = useSelector(state => state.configuration.input.blanket.blanketType);
  const primaryCoolantType = useSelector(state => state.configuration.input.blanket.primaryCoolantType);
  const secondaryCoolantType = useSelector(state => state.configuration.input.blanket.secondaryCoolantType);
  const neutronMultiplier = useSelector(state => state.configuration.input.blanket.neutronMultiplier);
  const structure = useSelector(state => state.configuration.input.blanket.structure);

  const isFormValid = () => {
    return firstWallType !== '' &&
      blanketType !== '' &&
      primaryCoolantType !== '' &&
      secondaryCoolantType !== '' &&
      neutronMultiplier !== '' &&
      structure !== '';
  };

  const handleBack = () => {
    navigate("/configurations/power")
  }

  const handleNext = () => {
    if (isFormValid()) {
      setValidationError(false)
      navigate("/configurations/materials")
    } else {
      setValidationError(true);
    }
  }

  function empty(value) {
    return value === '';
  }

  return (
    <Container>
      <ContentCard>
        <Title>Blanket Configuration</Title>
        <Form>
        <DropdownContainer>
          <LabelWrapper>
            <Label htmlFor="firstWallType">First Wall Type:</Label>
            <InfoTooltip infoText="Material of the first wall." id='blanket-reactor-type-tooltip'/>
          </LabelWrapper>
          { empty(firstWallType) && validationError && <ValidationError>Please select a first wall type.</ValidationError> }
          <Dropdown
            id="firstWallType"
            value={firstWallType}
            onChange={(e) => dispatch(setFirstWallType(e.target.value))}
          >
            <option value="">Select a first wall type</option>
            {FIRST_WALL_TYPE_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>{option.display}</option>
            ))}
          </Dropdown>
        </DropdownContainer>
        <DropdownContainer>
          <LabelWrapper>
            <Label htmlFor="blanketType">Blanket Type:</Label>
            <InfoTooltip infoText="Material of the blanket." id='blanket-material-type-tooltip'/>
          </LabelWrapper>
          { empty(blanketType) && validationError && <ValidationError>Please select a blanket type.</ValidationError> }
          <Dropdown
            id="blanketType"
            value={blanketType}
            onChange={(e) => dispatch(setBlanketType(e.target.value))}
          >
            <option value="">Select a blanket type</option>
            {BLANKET_TYPE_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>{option.display}</option>
            ))}
          </Dropdown>
        </DropdownContainer>
        <DropdownContainer>
          <LabelWrapper>
            <Label htmlFor="primaryCoolantType">Primary Coolant Type:</Label>
            <InfoTooltip infoText="Material of the primary coolant." id='primary-coolant-material-tooltip'/>
          </LabelWrapper>
          { empty(primaryCoolantType) && validationError && <ValidationError>Please select a primary coolant type.</ValidationError> }
          <Dropdown
            id="primaryCoolantType"
            value={primaryCoolantType}
            onChange={(e) => dispatch(setPrimaryCoolantType(e.target.value))}
          >
            <option value="">Select a primary coolant type</option>
            {COOLANT_TYPE_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>{option.display}</option>
            ))}
          </Dropdown>
        </DropdownContainer>
        <DropdownContainer>
          <LabelWrapper>
            <Label htmlFor="secondaryCoolantType">Secondary Coolant Type:</Label>
            <InfoTooltip infoText="Material of the secondary coolant." id='secondary-coolant-material-tooltip'/>
          </LabelWrapper>
          { empty(secondaryCoolantType) && validationError && <ValidationError>Please select a secondary coolant type.</ValidationError> }
          <Dropdown
            id="secondaryCoolantType"
            value={secondaryCoolantType}
            onChange={(e) => dispatch(setSecondaryCoolantType(e.target.value))}
          >
            <option value="">Select a secondary coolant type</option>
            {SECONDARY_COOLANT_TYPE_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>{option.display}</option>
            ))}
          </Dropdown>
        </DropdownContainer>
        <DropdownContainer>
          <LabelWrapper>
            <Label htmlFor="neutronMultiplier">Neutron Multiplier:</Label>
            <InfoTooltip infoText="Material for the neutron multiplication for tritium breeding in the blanket." id='neutron-multiplier-tooltip'/>
          </LabelWrapper>
          { empty(neutronMultiplier) && validationError && <ValidationError>Please select a neutron multiplier.</ValidationError> }
          <Dropdown
            id="neutronMultiplier"
            value={neutronMultiplier}
            onChange={(e) => dispatch(setNeutronMultiplier(e.target.value))}
          >
            <option value="">Select a neutron multiplier</option>
            {NEUTRON_MULTIPLIER_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>{option.display}</option>
            ))}
          </Dropdown>
        </DropdownContainer>
        <DropdownContainer>
          <LabelWrapper>
            <Label htmlFor="structure">Structural Material:</Label>
            <InfoTooltip infoText="Structural material for the power core." id='structure-tooltip'/>
          </LabelWrapper>
          { empty(structure) && validationError && <ValidationError>Please select a structural material.</ValidationError> }
          <Dropdown
            id="structure"
            value={structure}
            onChange={(e) => dispatch(setStructure(e.target.value))}
          >
            <option value="">Select a structural material</option>
            {STRUCTURAL_MATERIAL_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>{option.display}</option>
            ))}
          </Dropdown>
        </DropdownContainer>
        <ButtonsWrapper>
          <BackButton type="button" onClick={handleBack}>Back</BackButton>
          <Button type="button" onClick={handleNext}>Next</Button>
        </ButtonsWrapper>
        </Form>
      </ContentCard>
    </Container>
  )
};

export default BlanketConfiguration;